import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  EmptyState,
  PageHeader,
  STATUS_LABELS,
  StatusBadge,
} from "../../../components/dashboard/DashboardUI";
import { fetchOperatorProcedures } from "../../../redux/actions/ProcedureActions";
import { REQUEST_TYPES, requestTitle } from "../../../utils/requestDisplay";
import { DETAIL_PAGES, detailState } from "../../../utils/operatorPages";

const STATUS_FILTERS = [
  "paid",
  "in_progress",
  "fulfilled",
  "failed",
  "initiated",
];

const clientName = (request) =>
  [request.user?.firstname, request.user?.lastname].filter(Boolean).join(" ") ||
  request.company?.businessName ||
  "—";

function OperatorProcedures() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { procedures } = useSelector((state) => state.procedures);

  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [type, setType] = useState("");

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.resolve(
      dispatch(fetchOperatorProcedures(user.token, user.country)),
    ).finally(() => setLoading(false));
  }, [user, dispatch]);

  const requests = (procedures || [])
    .filter((request) => DETAIL_PAGES[request.type])
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  const types = [...new Set(requests.map((request) => request.type))];

  const term = search.trim().toLowerCase();
  const filtered = requests.filter(
    (request) =>
      // Unpaid orders stay hidden unless explicitly requested
      (status ? request.status === status : request.status !== "initiated") &&
      (!type || request.type === type) &&
      (!term ||
        request.reference?.toLowerCase().includes(term) ||
        clientName(request).toLowerCase().includes(term) ||
        requestTitle(request).toLowerCase().includes(term)),
  );

  const toProcess = requests.filter((request) => request.status === "paid");

  const open = (request) =>
    navigate(DETAIL_PAGES[request.type], { state: detailState(request) });

  return (
    <>
      <PageHeader
        title="Démarches clients"
        lead={
          toProcess.length
            ? `${toProcess.length} démarche${
                toProcess.length > 1 ? "s" : ""
              } payée${toProcess.length > 1 ? "s" : ""} en attente de prise en charge.`
            : "Toutes les démarches payées sont prises en charge."
        }
      />

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher une référence, un client ou une démarche"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Rechercher"
          />
          <select
            value={type}
            onChange={(event) => setType(event.target.value)}
            aria-label="Type de démarche"
          >
            <option value="">Tous les types</option>
            {types.map((value) => (
              <option key={value} value={value}>
                {REQUEST_TYPES[value] || value}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(event) => setStatus(event.target.value)}
            aria-label="Statut"
          >
            <option value="">Payées, en cours, terminées…</option>
            {STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {STATUS_LABELS[value]}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des démarches…</p>
        ) : filtered.length === 0 ? (
          <EmptyState title="Aucune démarche">
            Aucune démarche ne correspond à ces critères.
          </EmptyState>
        ) : (
          <div className="dash-table-wrap">
            <table className="dash-table is-responsive">
              <thead>
                <tr>
                  <th>Démarche</th>
                  <th>Client</th>
                  <th>Référence</th>
                  <th>Date</th>
                  <th>Statut</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((request) => (
                  <tr key={request.reference}>
                    <td>
                      <span className="dash-cell-title">
                        {requestTitle(request)}
                      </span>
                    </td>
                    <td data-label="Client">{clientName(request)}</td>
                    <td data-label="Référence">#{request.reference}</td>
                    <td data-label="Date">
                      {moment(request.createdAt).format("DD/MM/YYYY")}
                    </td>
                    <td data-label="Statut">
                      <StatusBadge status={request.status} />
                    </td>
                    <td>
                      <div className="dash-row-actions">
                        <button
                          type="button"
                          className="dash-link-button"
                          onClick={() => open(request)}
                        >
                          Ouvrir
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

export default OperatorProcedures;
